import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import Growie from "../components/Growie";
import PasswordMeter from "../components/PasswordMeter";
import { GameButton, GameCard, GameHero } from "../components/game";
import AppHandoff from "../components/AppHandoff";
import { athleteAges } from "../lib/athleteAge";

type JoinInfo = {
  token: string;
  team: { id: string; name: string; age_group: string | null };
  club: { id: string; name: string; logo_url: string | null };
  sport: { id: string; name: string } | null;
};

type NameCheck = { state: "idle" | "checking" | "free" | "taken"; message?: string };

const field =
  "w-full px-4 py-3 text-[15px] bg-white border border-slate-200 rounded-2xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

const label = "block mb-1.5 text-[13px] font-bold text-slate-600";

const RELATIONSHIPS = [
  { value: "mother", label: "Mamă" },
  { value: "father", label: "Tată" },
  { value: "guardian", label: "Tutore" },
];

/** The range a club actually fields. Outside it, the link is the wrong one. */
// 2 to 18 — see lib/athleteAge. The list used to start at six, which was
// the real limit on how young a child could be enrolled.
const AGES = athleteAges();

/**
 * The page a shared team link opens.
 *
 * Reached from a WhatsApp group, so it is opened by someone with no account,
 * no context and no patience: it says which team of which club this is before
 * asking for anything, explains in three lines what they and their child
 * actually get, and says plainly that the club still has to approve.
 *
 * Dressed as the app rather than as a sign-up form, because it is a parent's
 * first sight of iGROWth and the next thing they will see is the app itself.
 */
export default function JoinTeam() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { token: authToken } = useAuth();

  const [info, setInfo] = useState<JoinInfo | null>(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<{ club: string; team: string; childLogin: string } | null>(null);

  const [parentName, setParentName] = useState("");
  const [relationship, setRelationship] = useState("mother");
  const [parentEmail, setParentEmail] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentPassword, setParentPassword] = useState("");

  const [childName, setChildName] = useState("");
  const [childGender, setChildGender] = useState("");
  const [childAge, setChildAge] = useState("");
  const [childIdMode, setChildIdMode] = useState<"username" | "email">("username");
  const [childUsername, setChildUsername] = useState("");
  const [childEmail, setChildEmail] = useState("");
  const [childPassword, setChildPassword] = useState("");
  const [consent, setConsent] = useState(false);

  const [nameCheck, setNameCheck] = useState<NameCheck>({ state: "idle" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<JoinInfo>(`/public/join/${token}`);
        setInfo(data);
      } catch (e: any) {
        setLoadError(e?.response?.data?.error || "Linkul nu mai este valabil.");
      }
      setLoading(false);
    })();
  }, [token]);

  /**
   * Check the username while the parent types.
   *
   * Debounced, and every answer is tagged with the text it was asked about, so
   * a slow reply for "and" cannot overwrite the verdict for "andrei".
   */
  const latestQuery = useRef("");
  useEffect(() => {
    const value = childUsername.trim().toLowerCase();
    latestQuery.current = value;

    if (childIdMode !== "username" || value.length < 3) {
      setNameCheck({ state: "idle" });
      return;
    }

    setNameCheck({ state: "checking" });
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get<{ available: boolean; error?: string }>(
          `/public/join/${token}/username`,
          { params: { value } }
        );
        if (latestQuery.current !== value) return;
        setNameCheck(
          data.available
            ? { state: "free", message: "Username disponibil." }
            : { state: "taken", message: data.error || "Username-ul este deja folosit." }
        );
      } catch {
        // A failed check must not block the form; the server checks again on
        // submit and says so there.
        if (latestQuery.current === value) setNameCheck({ state: "idle" });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [childUsername, childIdMode, token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!consent) return setError("Confirmă acordul privind datele pentru a continua.");
    if (childIdMode === "username" && nameCheck.state === "taken") {
      return setError("Alege alt username pentru copil — acesta este deja folosit.");
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/join/${token}`, {
        consent: true,
        parent: {
          name: parentName.trim(),
          relationship,
          email: parentEmail.trim(),
          phone: parentPhone.trim() || undefined,
          password: parentPassword,
        },
        child: {
          first_name: childName.trim(),
          gender: childGender,
          age: Number(childAge),
          username: childIdMode === "username" ? childUsername.trim() : undefined,
          email: childIdMode === "email" ? childEmail.trim() : undefined,
          password: childPassword,
        },
      });
      setDone({ club: data.club, team: data.team, childLogin: data.child_login });
    } catch (e: any) {
      const body = e?.response?.data;
      if (body?.needs_login) {
        // They already have an account. Proving it is the sign-in's job, so
        // they go there and come back to the authenticated half of the flow.
        navigate(`/login?next=${encodeURIComponent(`/join/${token}`)}`);
        return;
      }
      setError(body?.error || "Nu am putut finaliza înscrierea.");
    }
    setSubmitting(false);
  };

  // Already signed in: the public form would refuse their email anyway, so
  // send them straight to the half of the flow that knows who they are.
  useEffect(() => {
    if (authToken && info && !done) {
      navigate(`/join/${token}/claim`, { replace: true });
    }
  }, [authToken, info, done, navigate, token]);

  const title = useMemo(() => {
    if (!info) return "";
    const sport = info.sport ? ` (${info.sport.name})` : "";
    return `Invitație în echipa ${info.team.name}${sport} a clubului ${info.club.name}`;
  }, [info]);

  if (loading) {
    return (
      <Shell>
        <GameCard className="p-6">
          <p className="text-sm text-slate-500">Se încarcă...</p>
        </GameCard>
      </Shell>
    );
  }

  if (loadError || !info) {
    return (
      <Shell>
        <GameCard className="p-6 text-center">
          <Growie pose="sad" className="w-20 h-20 mx-auto" />
          <h1 className="mt-2 text-xl font-extrabold text-slate-800">Link indisponibil</h1>
          <p className="mt-2 text-sm text-slate-600">{loadError}</p>
          <Link to="/login" className="block mt-5">
            <GameButton type="button">Mergi la autentificare</GameButton>
          </Link>
        </GameCard>
      </Shell>
    );
  }

  if (done) return <JoinDone done={done} />;

  return (
    <Shell
      hero={
        <div className="flex items-start gap-3">
          {info.club.logo_url ? (
            <img
              src={info.club.logo_url}
              alt=""
              className="object-cover w-12 h-12 bg-white rounded-2xl shrink-0"
            />
          ) : (
            <Growie pose="wave" className="w-12 h-12 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-white/70">
              Invitație
            </p>
            <h1 className="text-[19px] font-extrabold leading-snug break-words">{title}</h1>
            <p className="mt-1.5 text-[13px] text-white/80">
              Completează datele tale și ale copilului tău.
            </p>
          </div>
        </div>
      }
    >
      <GameCard className="px-4 py-3">
        <p className="text-[13px] text-slate-600">
          Clubul confirmă înscrierea înainte ca el să apară în echipă
          {info.team.age_group ? ` (grupa ${info.team.age_group})` : ""}.
        </p>
      </GameCard>

      <Benefits clubName={info.club.name} />

      {error && (
        <div className="px-4 py-3 text-sm border rounded-2xl bg-rose-50 border-rose-200 text-rose-700">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <GameCard className="p-5">
          <fieldset className="space-y-3">
            <legend className="mb-1 text-[15px] font-extrabold text-slate-800">
              Datele tale (părinte)
            </legend>
            <div>
              <label className={label} htmlFor="p-name">
                Nume și prenume
              </label>
              <input
                id="p-name"
                required
                autoComplete="name"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="p-rel">
                Ce ești pentru copil
              </label>
              <select
                id="p-rel"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                className={field}
              >
                {RELATIONSHIPS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="p-email">
                Email
              </label>
              <input
                id="p-email"
                type="email"
                required
                autoComplete="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="p-phone">
                Telefon <span className="font-normal text-slate-400">(opțional)</span>
              </label>
              <input
                id="p-phone"
                autoComplete="tel"
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="p-pass">
                Parolă
              </label>
              <input
                id="p-pass"
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={parentPassword}
                onChange={(e) => setParentPassword(e.target.value)}
                className={field}
              />
              <PasswordMeter value={parentPassword} />
            </div>
          </fieldset>
        </GameCard>

        <GameCard className="p-5">
          <fieldset className="space-y-3">
            <legend className="mb-1 text-[15px] font-extrabold text-slate-800">
              Datele copilului
            </legend>
            <div>
              <label className={label} htmlFor="c-name">
                Nume și prenume
              </label>
              <input
                id="c-name"
                required
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder="ex: Popescu Andrei"
                className={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="c-gender">
                Băiat sau fată
              </label>
              <select
                id="c-gender"
                required
                value={childGender}
                onChange={(e) => setChildGender(e.target.value)}
                className={field}
              >
                <option value="">Alege...</option>
                <option value="male">Băiat</option>
                <option value="female">Fată</option>
              </select>
            </div>
            <div>
              <label className={label} htmlFor="c-age">
                Vârsta
              </label>
              <select
                id="c-age"
                required
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
                className={field}
              >
                <option value="">Alege vârsta</option>
                {AGES.map((a) => (
                  <option key={a} value={a}>
                    {a} ani
                  </option>
                ))}
              </select>
            </div>

            <div>
              <span className={label}>Cum se va autentifica în aplicație?</span>
              <p className="mb-2 -mt-1 text-xs text-slate-400">
                Dacă nu are email propriu, alege opțiunea prin username.
              </p>
              <div className="inline-flex gap-1 p-1 mb-2 bg-slate-100 rounded-2xl">
                {(["username", "email"] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setChildIdMode(mode)}
                    aria-pressed={childIdMode === mode}
                    className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                      childIdMode === mode
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    {mode === "username" ? "Prin Username" : "Prin Email"}
                  </button>
                ))}
              </div>
              {childIdMode === "username" ? (
                <>
                  <input
                    aria-label="Username copil"
                    required
                    value={childUsername}
                    onChange={(e) => setChildUsername(e.target.value)}
                    placeholder="ex: andrei.campion"
                    autoComplete="off"
                    aria-invalid={nameCheck.state === "taken"}
                    className={`${field} ${
                      nameCheck.state === "taken"
                        ? "border-rose-400 focus:ring-rose-400"
                        : nameCheck.state === "free"
                        ? "border-emerald-400 focus:ring-emerald-400"
                        : ""
                    }`}
                  />
                  <p
                    aria-live="polite"
                    className={`mt-1 text-xs ${
                      nameCheck.state === "taken"
                        ? "text-rose-600"
                        : nameCheck.state === "free"
                        ? "text-emerald-600"
                        : "text-slate-400"
                    }`}
                  >
                    {nameCheck.state === "checking"
                      ? "Verific dacă este liber..."
                      : nameCheck.message || "Litere mici, cifre, _ sau . — minim 3 caractere."}
                  </p>
                </>
              ) : (
                <input
                  aria-label="Email copil"
                  type="email"
                  required
                  value={childEmail}
                  onChange={(e) => setChildEmail(e.target.value)}
                  className={field}
                />
              )}
            </div>

            <div>
              <label className={label} htmlFor="c-pass">
                Parola copilului
              </label>
              <input
                id="c-pass"
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={childPassword}
                onChange={(e) => setChildPassword(e.target.value)}
                className={field}
              />
              <PasswordMeter value={childPassword} />
            </div>
          </fieldset>
        </GameCard>

        <GameCard className="p-5">
          <label className="flex items-start gap-2.5 text-[13px] leading-relaxed text-slate-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded-md border-slate-300"
            />
            <span>
              Sunt părintele/tutorele copilului și sunt de acord ca datele noastre să fie prelucrate
              de {info.club.name} și de aplicația iGrowth, conform{" "}
              <Link to="/privacy" target="_blank" className="font-bold text-indigo-600 underline">
                politicii de confidențialitate
              </Link>
              .
            </span>
          </label>

          <div className="mt-4">
            <GameButton type="submit" disabled={submitting}>
              {submitting ? "Se trimite..." : "Trimite cererea"}
            </GameButton>
          </div>
        </GameCard>
      </form>
    </Shell>
  );
}

/**
 * What the family actually gets out of this.
 *
 * Someone who has just been sent a link in a group chat is being asked for
 * their child's data by an app they have never heard of. Six concrete lines —
 * what the child sees, what the parent sees — do more for that decision than
 * any amount of enthusiasm.
 */
function Benefits({ clubName }: { clubName: string }) {
  return (
    <GameCard className="p-4">
      <div className="flex items-center gap-3">
        <Growie pose="cheer" className="w-12 h-12 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-slate-800">{clubName} folosește iGROWth</p>
          <p className="text-xs text-slate-500">
            Aplicația prin care clubul ține legătura cu voi, iar copilul își vede progresul.
          </p>
        </div>
      </div>

      <div className="grid gap-3 mt-4 sm:grid-cols-2">
        <Benefit
          title="Pentru copil"
          tone="indigo"
          items={[
            "Vede antrenamentele și primește puncte pentru prezență",
            "Quiz-uri și provocări care îi urmăresc progresul",
            "Clasamente și insigne alături de colegii de echipă",
          ]}
          icon={
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          }
        />
        <Benefit
          title="Pentru tine"
          tone="emerald"
          items={[
            "Programul antrenamentelor și anunțurile clubului, într-un loc",
            "Vezi prezența, evoluția și rezultatele copilului",
            "Plățile și cotizațiile, clar, fără mesaje pierdute prin grup",
          ]}
          icon={
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
            />
          }
        />
      </div>
    </GameCard>
  );
}

function Benefit({
  title,
  items,
  icon,
  tone,
}: {
  title: string;
  items: string[];
  icon: React.ReactNode;
  tone: "indigo" | "emerald";
}) {
  const ring = tone === "indigo" ? "bg-indigo-50 text-indigo-600" : "bg-emerald-50 text-emerald-600";
  const tick = tone === "indigo" ? "text-indigo-500" : "text-emerald-500";
  return (
    <div className="p-3 rounded-2xl bg-slate-50">
      <div className="flex items-center gap-2">
        <span className={`flex items-center justify-center w-8 h-8 rounded-xl ${ring}`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon}
          </svg>
        </span>
        <p className="text-sm font-extrabold text-slate-800">{title}</p>
      </div>
      <ul className="mt-2 space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex gap-2 text-xs leading-relaxed text-slate-600">
            <svg
              className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tick}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The page after the form.
 *
 * The moment a parent has just handed over their child's data is the moment to
 * be warm and concrete, not to congratulate them with a tick. So: Growie, what
 * happens next in plain words, and the one thing they must actually do — open
 * the email, within 24 hours.
 */
function JoinDone({ done }: { done: { club: string; team: string; childLogin: string } }) {
  return (
    <Shell
      hero={
        <div className="text-center">
          <Growie pose="cheer" className="w-24 h-24 mx-auto" title="Growie, mascota iGROWth" />
          <h1 className="mt-2 text-2xl font-extrabold">Bine ai venit în echipă!</h1>
          <p className="mt-2 text-[13px] leading-relaxed text-white/85">
            Am trimis cererea către <strong>{done.club}</strong>. Antrenorul echipei{" "}
            <strong>{done.team}</strong> o confirmă, și de acolo începe totul.
          </p>
        </div>
      }
    >
      <GameCard className="p-4 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 mt-0.5 text-amber-600 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-amber-900">Un singur pas a mai rămas</p>
            <p className="mt-1 text-[13px] leading-relaxed text-amber-800">
              Ți-am trimis un email de confirmare. Deschide-l și apasă butonul din el —{" "}
              <strong>linkul expiră în 24 de ore</strong>. Dacă nu îl găsești, caută și în Spam sau
              Promoții.
            </p>
          </div>
        </div>
      </GameCard>

      <GameCard className="p-4">
        <p className="text-sm font-extrabold text-slate-800">Contul copilului e gata</p>
        <p className="mt-1 mb-4 text-[13px] leading-relaxed text-slate-600">
          Se autentifică cu <strong className="break-all">{done.childLogin}</strong> și parola
          aleasă de tine. Instalează-i aplicația pe telefon și lasă-l să își vadă echipa,
          antrenamentele și punctele.
        </p>
        {/* The next step is the app, not a web login — so the stores are the
            thing on this card, rather than a button that sends them back to a
            browser they will leave in a minute anyway. */}
        <AppHandoff
          tone="light"
          path="/app"
          title="Descarcă aplicația"
          subtitle="Alege magazinul telefonului tău."
        />
      </GameCard>

      <p className="text-xs text-center text-slate-400">
        Te anunțăm imediat ce clubul confirmă înscrierea.
      </p>
    </Shell>
  );
}

/**
 * The app's own chrome, not a sign-up page's.
 *
 * Dark header, gradient hero spilling out of it, white cards on a light field —
 * the same shape every screen inside iGROWth has, so the first thing a parent
 * sees already looks like the thing they are about to install.
 */
function Shell({ children, hero }: { children: React.ReactNode; hero?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0b1120]">
        <div className="flex items-center justify-center h-12 max-w-md mx-auto">
          <span className="text-lg font-extrabold text-white">
            <span className="text-emerald-400">i</span>GROWth
          </span>
        </div>
      </header>

      {hero && (
        <GameHero flow className="px-4 pt-3 pb-6 rounded-br-3xl">
          <div className="max-w-md mx-auto">{hero}</div>
        </GameHero>
      )}

      <div className="max-w-md px-4 py-5 mx-auto space-y-4">{children}</div>
    </div>
  );
}
